// What a filter editor shows and when it commits. The committed value lives in the table state; the numeric
// and date drafts live here, in one store the filter bar owns, so a draft survives its panel closing and its
// trigger moving into More filters, and one reset can drop every draft at once.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { formatDate, formatNumber } from '../format'
import {
  FILTER_ERRORS,
  checkRangeDraft,
  enumSelection,
  filterMode,
  filterTypeOf,
  isFilterActive,
  refinementOn,
} from '../query'
import { ACTIONS } from '../types'

export const NUMERIC_COMMIT_MS = 900

export const MODE_LABELS = {
  numeric: [
    ['equal', 'Equals'],
    ['greaterThan', 'Greater than'],
    ['lessThan', 'Less than'],
    ['between', 'Between'],
  ],
  date: [
    ['on', 'Is'],
    ['before', 'Is before'],
    ['after', 'Is after'],
    ['between', 'Is between'],
  ],
}

// the lowercase mode a trigger summary reads before its value; a date drops its leading "is"
const SUMMARY_WORDS = {
  equal: 'equals',
  greaterThan: 'greater than',
  lessThan: 'less than',
  on: '',
  before: 'before',
  after: 'after',
}

const text = value =>
  value === null || value === undefined ? '' : String(value)

/**
 * The editor's starting draft for a committed numeric or date value (or none): Equals and Is by default.
 * Bounds are the text the inputs show; `bad` marks a bound the browser could not read.
 */
export const draftFrom = (type, committed) => {
  const value =
    committed && typeof committed === 'object' && 'mode' in committed
  return {
    mode: filterMode(type, value ? committed.mode : null),
    from: value ? text(committed.from) : '',
    to: value ? text(committed.to) : '',
    bad: {},
  }
}

const boundError = type =>
  type === 'date' ? FILTER_ERRORS.date : FILTER_ERRORS.number

/** checkRangeDraft, plus the bounds the browser itself reported unreadable (a partial date). */
export const checkDraft = (type, draft) => {
  const bad = (draft && draft.bad) || {}
  const between = filterMode(type, draft && draft.mode) === 'between'
  if (bad.from || (between && bad.to))
    return { value: null, error: boundError(type) }
  return checkRangeDraft(type, draft)
}

// Which inputs an error belongs to: a reversed range marks the end, an unreadable bound marks itself.
export const invalidBounds = (type, draft, error) => {
  if (!error) return { from: false, to: false }
  if (error === FILTER_ERRORS.range) return { from: false, to: true }
  const bad = (draft && draft.bad) || {}
  const unreadable = value =>
    Boolean(checkRangeDraft(type, { mode: 'between', from: value }).error)
  const between = filterMode(type, draft && draft.mode) === 'between'
  return {
    from: Boolean(bad.from) || unreadable(draft && draft.from),
    to: between && (Boolean(bad.to) || unreadable(draft && draft.to)),
  }
}

const canonical = value => {
  if (Array.isArray(value) || (value && Array.isArray(value.selected)))
    return {
      selected: enumSelection(value).slice().sort(),
      refined: refinementOn(value),
    }
  return {
    mode: value.mode,
    from: value.from ?? null,
    to: value.mode === 'between' ? value.to ?? null : null,
  }
}

/** Whether two filter values filter the same rows; every inactive value is the same. */
export const sameFilter = (a, b) => {
  const activeA = isFilterActive(a)
  const activeB = isFilterActive(b)
  if (!activeA || !activeB) return activeA === activeB
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

const formatBound = (type, column, value, locale) =>
  type === 'date'
    ? formatDate(value, locale, column.dateStyle)
    : formatNumber(value, column.format, locale)

/**
 * A committed filter's summary, for its trigger: `{n} selected`, or the lowercase mode and value, with Between
 * reading `{start} – {end}`, `from {start}` or `up to {end}`. Null while the filter is inactive.
 */
export const filterSummary = (column, value, locale) => {
  if (!isFilterActive(value)) return null
  const type = filterTypeOf(column)
  if (type !== 'numeric' && type !== 'date')
    return `${enumSelection(value).length} selected`
  const mode = filterMode(type, value.mode)
  const has = bound => bound !== null && bound !== undefined
  const format = bound => formatBound(type, column, bound, locale)
  if (mode === 'between') {
    if (has(value.from) && has(value.to))
      return `${format(value.from)} – ${format(value.to)}`
    if (has(value.from)) return `from ${format(value.from)}`
    return `up to ${format(value.to)}`
  }
  const word = SUMMARY_WORDS[mode]
  return word ? `${word} ${format(value.from)}` : format(value.from)
}

/** The text a trigger shows: its label, then its summary while it is active. */
export const triggerText = (column, value, locale) => {
  const summary = filterSummary(column, value, locale)
  return summary ? `${column.label}: ${summary}` : column.label
}

/** Commits a filter value unless it filters exactly what is already committed. */
export const commitFilter = (dispatch, committed, key, value) => {
  if (sameFilter(committed, value)) return false
  dispatch({ type: ACTIONS.filterSet, key, value, reason: 'filter' })
  return true
}

// ---- the store ----

export const DraftsContext = createContext(null)

/**
 * The bar's draft store. A record is {draft, error, basis}: `basis` is the committed value the draft was
 * written against, and a record whose basis no longer matches the committed value (a controlled change, a
 * column that went away) is ignored. Timers are the numeric 900ms commits, one per column.
 * @param {(key: string) => *} committedOf
 */
export const useDraftStore = committedOf => {
  const records = useRef({})
  const timers = useRef({})
  const committed = useRef(committedOf)
  committed.current = committedOf
  const [version, setVersion] = useState(0)

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), [])

  const store = useMemo(() => {
    const bump = () => setVersion(v => v + 1)
    const cancel = key => {
      clearTimeout(timers.current[key])
      delete timers.current[key]
    }
    return {
      committed: key => committed.current(key),
      record: key => {
        const record = records.current[key]
        return record && sameFilter(record.basis, committed.current(key))
          ? record
          : null
      },
      write: (key, { draft, error = null, basis }) => {
        records.current[key] = {
          draft,
          error,
          basis: basis === undefined ? committed.current(key) : basis,
        }
        bump()
      },
      drop: key => {
        cancel(key)
        if (records.current[key]) {
          delete records.current[key]
          bump()
        }
      },
      // keeps the records of these columns only
      retain: keys => {
        const gone = Object.keys(records.current)
          .concat(Object.keys(timers.current))
          .filter(key => !keys.includes(key))
        gone.forEach(key => {
          cancel(key)
          delete records.current[key]
        })
        if (gone.length) bump()
      },
      dropAll: () => {
        Object.keys(timers.current).forEach(cancel)
        if (Object.keys(records.current).length) {
          records.current = {}
          bump()
        }
      },
      schedule: (key, run, ms) => {
        cancel(key)
        timers.current[key] = setTimeout(() => {
          delete timers.current[key]
          run()
        }, ms)
      },
      cancel,
    }
  }, [])

  return useMemo(() => ({ store, version }), [store, version])
}

export const useDrafts = () => {
  const value = useContext(DraftsContext)
  if (!value) throw new Error('useDrafts must be called inside a FilterBar')
  return value.store
}
